'use client'

import {useLocation} from 'react-router-dom'
import {SidebarNavLink} from 'src/components/portal-migration/sidebar-nav-link'
import useWindowSize from 'src/components/slider/hooks/useWindowSize'
import {useAppSidebar} from 'src/layouts/main-layout/app-sidebar/app-sidebar-context'
import {NavigationMenuType} from 'src/layouts/main-layout/app-sidebar/navigation/types/navigation-menu.type'
import {isPathActive} from 'src/layouts/main-layout/app-sidebar/utils/is-path-active.helper'
import {cn} from 'src/layouts/main-layout/app-sidebar/utils/utils'
import {tv} from 'tailwind-variants'
import {AppSidebarNavIcon} from './app-sidebar-nav-icon'

const subnavWrapVariants = tv({
  base: ['relative'],
  variants: {
    variant: {
      inline: 'mt-1',
      popover: 'space-y-1 px-1 pb-1',
    },
  },
  defaultVariants: {
    variant: 'inline',
  },
})

const subnavLineVariants = tv({
  base: [
    'relative',
    'before:absolute before:left-6 before:-translate-x-1/2 before:h-full',
    "before:bg-sidebar-foreground before:content-['']",
    'before:w-px',
    'transition-[before-width] duration-200',
  ],
  variants: {
    active: {
      true: 'before:w-0.5 before:rounded-full',
      false: 'before:w-px',
    },
  },
})

const subItemVariants = tv({
  base: [
    'relative flex w-full items-center text-sm',
    'transition-all duration-150 ease-out',
    'no-underline',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  ],
  variants: {
    variant: {
      inline:
        'h-12 rounded-lg pl-12 pr-4 text-sidebar-foreground visited:text-sidebar-foreground hover:bg-[#182B4466] hover:text-sidebar-foreground active:text-sidebar-foreground',
      popover: [
        'h-9 justify-start gap-2.5 rounded-lg px-3 text-[13px] font-medium leading-none',
        'text-[#DCE7F4] visited:text-[#DCE7F4] hover:bg-[#1A2B42] hover:text-white active:text-[#DCE7F4]',
        'focus-visible:ring-[#2F9CFF]/50',
      ],
    },
    active: {
      true: 'text-sidebar-foreground font-bold',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'popover',
      active: true,
      className:
        'bg-[#1B304A] font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.035)] before:absolute before:left-0 before:top-1/2 before:h-6 before:w-0.5 before:-translate-y-1/2 before:rounded-full before:bg-[#2F9CFF] before:content-[""]',
    },
  ],
  defaultVariants: {
    variant: 'inline',
  },
})

interface Props {
  items: Pick<NavigationMenuType, 'id' | 'label' | 'route' | 'icon'>[]
  variant?: 'inline' | 'popover'
}

export function AppSidebarSubnav({items, variant = 'inline'}: Props) {
  const {pathname} = useLocation()
  const {toggleCollapsed} = useAppSidebar()

  const isChildActive = (route: string) => {
    return isPathActive(pathname, route)
  }

  const {width} = useWindowSize()
  const mobileWidth = width <= 767

  return (
    <div data-slot='sidebar-subnav' className={cn(subnavWrapVariants({variant}))}>
      {items.map((sub, index) => {
        const active = isChildActive(sub.route)

        return (
          <div key={sub.id}>
            <SidebarNavLink
              route={sub.route}
              title={variant === 'inline' ? sub.label : undefined}
              data-slot='sidebar-subitem'
              data-active={active ? active : undefined}
              style={{textDecoration: 'none'}}
              className={cn(subItemVariants({active, variant}), variant === 'inline' && subnavLineVariants({active}))}
              onNavigate={() => mobileWidth && toggleCollapsed()}>
              {variant === 'popover' && sub.icon && (
                <span className='flex size-4 shrink-0 items-center justify-center text-white/85'>
                  <AppSidebarNavIcon icon={sub.icon} active={active} className='size-4' />
                </span>
              )}
              <span className='min-w-0 flex-1 truncate text-left'>{sub.label}</span>
            </SidebarNavLink>
          </div>
        )
      })}
    </div>
  )
}
